import { Card, CardContent } from "@/components/ui/card";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  CheckSquare,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { useTaskSettings } from "@/hooks/use-task-settings";
import { DraggableStatusList } from "./DraggableStatusList";
import { EditStatusDialog } from "./EditStatusDialog";
import { DeleteStatusDialog } from "./DeleteStatusDialog";
import { CreateStatusDialog } from "./CreateStatusDialog";
import { TaskDialog } from "./TaskDialog";
import { WorkflowManager } from "./WorkflowManager";

export function TaskSettings() {
  const {
    statusesWithTasks,
    isLoading,
    error,
    hasCompany,
    isAdmin,
    selectedPipelineId,
    setSelectedPipelineId,

    // Status Dialogs
    dialogOpen,
    deleteDialogOpen,
    createDialogOpen,
    editingStatus,
    statusToDelete,
    isDeleting,
    isCreating,
    isStatusUpdating,

    // Task Dialogs
    taskDialogOpen,
    activeTask,
    taskMode,
    isTaskSaving,

    // Actions
    handleEditStatus,
    handleSaveStatus,
    handleDeleteStatus,
    handleOpenDeleteDialog,
    handleOpenCreateDialog,
    handleCreateStatus,
    handleReorderStatuses,

    handleOpenCreateTask,
    handleOpenEditTask,
    handleSaveTask,
    handleDeleteTask,

    setEditingStatus,
    setDialogOpen,
    setDeleteDialogOpen,
    setCreateDialogOpen,
    setTaskDialogOpen,
  } = useTaskSettings();

  // Early returns for loading/error states
  if (!hasCompany) {
    return (
      <div className="max-w-7xl mx-auto">
        <Card>
          <CardContent className="py-12 text-center">
            <CheckSquare className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Company Found</h3>
            <p className="text-muted-foreground">
              You need to be associated with a company to view task workflows.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error Loading Statuses</AlertTitle>
          <AlertDescription>
            {error instanceof Error ? error.message : "Failed to load claim status templates"}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {!isAdmin && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>View Only</AlertTitle>
          <AlertDescription>
            You are viewing the claim workflow. Only administrators can make changes.
          </AlertDescription>
        </Alert>
      )}

      <WorkflowManager
        selectedPipelineId={selectedPipelineId}
        onSelect={setSelectedPipelineId}
        isAdmin={isAdmin}
      />

      <DraggableStatusList
        statuses={statusesWithTasks.sort((a, b) => a.displayOrder - b.displayOrder)}
        onEdit={handleEditStatus}
        onDelete={handleOpenDeleteDialog}
        onReorder={handleReorderStatuses}
        // Task handlers
        onAddTask={handleOpenCreateTask}
        onEditTask={handleOpenEditTask}
        onDeleteTask={handleDeleteTask}
        selectedPipelineId={selectedPipelineId}
        onSelectPipeline={setSelectedPipelineId}
        onCreateStatus={handleOpenCreateDialog}
        isAdmin={isAdmin}
      />

      <CreateStatusDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onConfirm={handleCreateStatus}
        isCreating={isCreating}
      />

      <EditStatusDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        status={editingStatus}
        onStatusChange={setEditingStatus}
        onSave={handleSaveStatus}
        isLoading={isStatusUpdating}
      />

      <TaskDialog
        open={taskDialogOpen}
        onOpenChange={setTaskDialogOpen}
        task={activeTask}
        mode={taskMode}
        onSave={handleSaveTask}
        isLoading={isTaskSaving}
      />

      <DeleteStatusDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        status={statusToDelete}
        onConfirm={handleDeleteStatus}
        isDeleting={isDeleting}
        totalActiveTemplates={statusesWithTasks.filter(s => s.isActive !== false).length}
      />
    </div>
  );
}
